import React from "react";
import { useTranslation } from "react-i18next";
import AppImageVideoUpload from "components/ui/AppImageVideoUpload";
import SaveBackButton from "components/ui/SaveBackButton";
import AppTextArea from "components/ui/AppTextArea";
import AppCheckbox from "components/ui/AppCheckbox";
import AppInput from "components/ui/AppInput";
import { CONSTANTS as C } from "config";
import { UTILS } from "utils";
import APIKit from "api/api/axiosSetup";

const ItemsForm = ({
  id,
  itemId,
  item,
  handleBasicInfo = () => {},
  handleValues = () => {},
  onSave = () => {},
  setIsDirty = () => {},
  setLoading = () => {},
  setItem = () => {},
  handleStockEnabledValues = () => {},
  content = null,
  onBack = null,
  showBackButton = true,
}) => {
  const { t } = useTranslation();
  const deleteRequest = async (file) => {
    setIsDirty(true);
    setLoading(true);
    try {
      const resp = await APIKit.delete("fms/remove-media-file-url", {
        data: {
          url: file?.url,
          itemId: itemId,
        },
      });
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const flattenObjectKeys = (obj, prefix = "") => {
    let keys = [];

    Object.entries(obj || {}).forEach(([key, value]) => {
      const newKey = prefix ? `${prefix}.${key}` : key;

      if (value && typeof value === "object" && !Array.isArray(value)) {
        keys = [...keys, ...flattenObjectKeys(value, newKey)];
      } else {
        keys.push(newKey);
      }
    });

    return keys;
  };

  React.useEffect(() => {
    const flatKeys = flattenObjectKeys(item?.errors);

    if (flatKeys.length > 0) {
      const firstErrorKey = flatKeys.find((key) => {
        const value = key.split(".").reduce((acc, k) => acc?.[k], item?.errors);
        return Array.isArray(value)
          ? value.some((v) => v !== "" && v !== null && v !== undefined)
          : value !== "" && value !== null && value !== undefined;
      });

      if (!firstErrorKey) return;

      const container = document.getElementById("appContainerItemForm");

      if (container) {
        const formattedId = firstErrorKey.replace(/\./g, "-"); // match DOM id
        const errorElement = container.querySelector(
          `[id="item-form-${formattedId}"]`
        );

        if (errorElement) {
          errorElement.scrollIntoView({
            behavior: "smooth",
            block: "end",
          });

          if (typeof errorElement.focus === "function") {
            errorElement.focus({ preventScroll: true });
          }
        }
      }

      setIsDirty(true);
    }
  }, [item?.errors]);

  return (
    <div className="relative h-full">
      <form id={id}>
        <div className="grid md:grid-cols-2 grid-cols-1  gap-4">
          <AppInput
            id="item-form-baseLanguage-title"
            label={t("Name")}
            placeholder={t("Enter Item Name")}
            value={item?.values?.baseLanguage?.title}
            onInput={(e) => handleBasicInfo("title", e.target.value)}
            mandatory={true}
            error={item?.errors?.baseLanguage?.title}
          />
          <AppInput
            id="item-form-price"
            type="number"
            label={t("Price") + " (" + t("AED") + ")"}
            placeholder={t("Enter Item Price")}
            value={item?.values?.price}
            onInput={(e) => handleValues("price", e.target.value)}
            error={item?.errors?.price}
          />
          <AppInput
            id="item-form-ingredients"
            label={t("Ingredients")}
            placeholder={t("Enter Item Ingredients")}
            value={item?.values?.baseLanguage?.ingredients}
            onInput={(e) => handleBasicInfo("ingredients", e.target.value)}
            error={item?.errors?.baseLanguage?.ingredients}
          />
          <AppInput
            id="item-form-sku"
            label={t("SKU (Stock Keeping Unit)")}
            placeholder={t("Enter Item SKU")}
            value={item?.values?.sku}
            onInput={(e) => handleValues("sku", e.target.value)}
            error={item?.errors?.sku}
          />
        </div>
        
        <div className="col-span-2">
          <AppImageVideoUpload
            id="item-form-itemImageURLs"
            label="Upload Item Image/Video"
            placeholder={t("Upload Item Image/Video")}
            value={item?.values?.itemImageURLs}
            onChange={(val, imageUrl) => {
              setItem((prevState) => ({
                ...prevState,
                values: {
                  ...prevState.values,
                  itemImageURLs: JSON.parse(JSON.stringify(val?.fileList)),
                },
              }));
            }}
            error={item?.errors?.itemImageURLs}
            maxCount={12}
            enableCrop={false}
            isImageUpload={false}
            action={C.BASE_URL + "user/uploadFile"}
            headers={{
              Authorization: `Bearer ${UTILS.getCurrentUser().token}`,
            }}
            setIsDirty={setIsDirty}
            setLoading={setLoading}
            deleteRequest={deleteRequest}
            itemId={itemId}
          />
        </div>
        <div className="col-span-2">
          <AppTextArea
            id="item-form-baseLanguage-description"
            label={t("Description")}
            placeholder={t("Enter Item Description")}
            value={item?.values?.baseLanguage?.description}
            onInput={(e) => handleBasicInfo("description", e.target.value)}
            error={item?.errors?.baseLanguage?.description}
          />
        </div>
        <div className="my-4 md:my-4">
          <AppCheckbox
            id="item-form-isStockEnabled"
            label={t("Stock Management")}
            checked={item?.values?.isStockEnabled}
            onChange={handleStockEnabledValues}
          />
        </div>
        
        <div className="grid md:grid-cols-2 grid-cols-1  gap-4">
          <AppInput
            id="item-form-availableStock"
            label={t("In Stock Quantity")}
            placeholder={t("Enter In Stock Quantity")}
            value={item?.values?.availableStock}
            onInput={(e) => handleValues("availableStock", e.target.value)}
            error={item?.errors?.availableStock}
            disabled={!item?.values?.isStockEnabled}
            type="number"
          />
          <AppInput
            id="item-form-lowStockThreshold"
            label={t("Low Stock Threshold")}
            placeholder={t("Enter Low Stock Threshold")}
            value={item?.values?.lowStockThreshold}
            onInput={(e) => handleValues("lowStockThreshold", e.target.value)}
            error={item?.errors?.lowStockThreshold}
            disabled={!item?.values?.isStockEnabled}
            type="number"
          />
        </div>
        
        <br />
      </form>
      {content ? <div className="my-4 w-full">{content}</div> : null}
      <SaveBackButton onSave={onSave} onBack={onBack} showBackButton={showBackButton} />
    </div>
  );
};

export default ItemsForm;
